import { ENV } from '../config/env';

interface TrialStatus {
  isInTrial: boolean;
  daysRemaining: number;
  trialEnd: string | null;
  reminderSent: boolean;
  planId: string | null;
  status: 'trialing' | 'not_in_trial' | string;
}

/**
 * Service for managing trial-related functionality
 */
export const trialService = {
  /**
   * Get the current user's trial status
   * @param authToken JWT token for authentication
   * @param userId Optional user id to pass to backend for compatibility
   * @returns TrialStatus object with trial information
   */
  async getTrialStatus(authToken: string, userId?: string): Promise<TrialStatus> {
    try {
      const url = userId
        ? `${ENV.API_URL}/api/trial/status?userId=${encodeURIComponent(userId)}`
        : `${ENV.API_URL}/api/trial/status`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Failed to get trial status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching trial status:', error);
      // Return default status if there's an error
      return {
        isInTrial: false,
        daysRemaining: 0,
        trialEnd: null,
        reminderSent: false,
        planId: null,
        status: 'not_in_trial'
      };
    }
  },

  /**
   * Start a new trial for the current user
   * @param authToken JWT token for authentication
   * @param planId The plan ID to start a trial for ('monthly' or 'annual')
   * @returns URL to redirect to for Stripe checkout
   */
  async startTrial(authToken: string, planId: string): Promise<string> {
    const response = await fetch(`${ENV.API_URL}/api/trial/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        planId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.details || 'Failed to start trial');
    }

    const data = await response.json();
    if (!data.url) {
      throw new Error('Invalid response from server');
    }

    return data.url;
  },
  
  /**
   * Format trial end date to a human-readable string
   * @param trialEnd Date string or timestamp
   * @returns Formatted date string
   */
  formatTrialEndDate(trialEnd: string | number | null): string {
    if (!trialEnd) return 'N/A';
    
    // Handle Unix timestamp (seconds since epoch)
    const date = typeof trialEnd === 'number' 
      ? new Date(trialEnd * 1000) 
      : new Date(trialEnd);
      
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },
  
  /**
   * Calculate days remaining until a date
   * @param endDate Date string or timestamp
   * @returns Number of days remaining (rounded up)
   */
  calculateDaysRemaining(endDate: string | number | null): number {
    if (!endDate) return 0;
    
    const end = typeof endDate === 'number' 
      ? new Date(endDate * 1000) 
      : new Date(endDate);
      
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays); // Ensure we never return negative days
  }
};

export default trialService;
